// Detail page's "Costs" section (Unleashed "Stock Enquiry" -> Costs tab
// parity, Stuart's 2026-08-26 full-parity follow-up ruling). Headline
// Average cost / Last cost come from the product-detail totals ProductDetailScreen
// already fetched (passed down rather than re-queried); the cost-history table
// re-fetches the SAME GET /reporting/movements?withRunningBalance=true rows
// ProductTransactionsTable shows (own query — this tab has no site/type/date
// filters, unlike Transactions, so its cache key and result set legitimately
// differ), reading the OPTIONAL `preAvgCostCentsPerUnit`/`postAvgCostCentsPerUnit`
// fields those rows may carry. Those bracket each movement's effect on the
// weighted-average cost; they're absent for movement types the backend
// doesn't cost (e.g. a same-site transfer) — formatMoneyCents already renders
// an em-dash for `null`/`undefined`, so no extra fallback logic is needed here.
//
// Pagination mirrors ProductTransactionsTable's own Previous/Next cursor idiom
// exactly (same self-contained-sibling convention this module's other files
// document) — duplicated rather than extracted into a shared component since
// each table's column set differs.
import { useState } from 'react';
import { Alert, Badge, Button, Group, Paper, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconCoin } from '@tabler/icons-react';
import type { BaseUom } from '@zebra/core';
import { EmptyState } from '../../../components/EmptyState';
import { MOVEMENT_TYPE_DESCRIPTIONS, MOVEMENT_TYPE_LABELS } from '../../../lib/enumLabels';
import { useProductMovements } from '../api';
import { formatCostPerUom, formatDate, formatDateTime, formatQty } from '../format';
import type { ProductDetailTotals } from '../types';

export function ProductCostHistoryTable({ productId, totals, baseUom }: { productId: string; totals: ProductDetailTotals; baseUom: BaseUom }) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  // Cursor is page-position state, not product-identity state — if the route
  // swaps $productId without remounting this component, a stale cursor from
  // the previous product must never reach the query. Reset it synchronously
  // during render (React's documented "adjusting state on a prop change"
  // pattern) rather than in a useEffect: an effect-based reset would still let
  // this render's `useProductMovements` call fire once with the stale cursor
  // before the effect corrects it on the next pass — this way the corrected
  // value is what the very first render for the new productId ever queries.
  const [cursorProductId, setCursorProductId] = useState(productId);
  if (productId !== cursorProductId) {
    setCursorProductId(productId);
    setCursors([undefined]);
  }
  const cursor = cursors[cursors.length - 1];

  const query = useProductMovements({ productId, cursor, limit: 50 });
  const rows = query.data?.items ?? [];

  return (
    <Stack gap="sm">
      <Group gap="md" wrap="wrap">
        <Paper withBorder radius="md" p="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Average cost
          </Text>
          <Text size="lg" fw={700}>
            {formatCostPerUom(totals.avgCostCentsPerUnit, baseUom)}
          </Text>
        </Paper>
        <Paper withBorder radius="md" p="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Last cost
          </Text>
          <Text size="lg" fw={700}>
            {formatCostPerUom(totals.lastCostCentsPerUnit, baseUom)}
          </Text>
          {totals.lastCostAt && (
            <Text size="xs" c="dimmed">
              as of {formatDate(totals.lastCostAt)}
            </Text>
          )}
        </Paper>
      </Group>

      <Title order={4}>Cost history</Title>

      {query.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load cost history.
        </Alert>
      )}

      <Table.ScrollContainer minWidth={780}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Reference</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Qty</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Unit cost</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Pre avg</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Post avg</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{formatDateTime(r.occurredAt)}</Table.Td>
                <Table.Td>
                  <Tooltip label={MOVEMENT_TYPE_DESCRIPTIONS[r.type]} openDelay={300}>
                    <Badge variant="light">{MOVEMENT_TYPE_LABELS[r.type]}</Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  {r.referenceType} {r.referenceId.slice(-6)}
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatQty(r.qty)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCostPerUom(r.unitCostCents, baseUom)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCostPerUom(r.preAvgCostCentsPerUnit, baseUom)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCostPerUom(r.postAvgCostCentsPerUnit, baseUom)}</Table.Td>
              </Table.Tr>
            ))}
            {!query.isLoading && !query.isError && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <EmptyState icon={IconCoin} title="No cost history for this product" hint="Nothing has been received or costed yet." />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group justify="space-between">
        <Button variant="default" size="sm" disabled={cursors.length <= 1} onClick={() => setCursors((prev) => prev.slice(0, -1))}>
          Previous page
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!query.data?.nextCursor}
          onClick={() => setCursors((prev) => [...prev, query.data?.nextCursor ?? undefined])}
        >
          Next page
        </Button>
      </Group>
    </Stack>
  );
}
